import { fetchWithAuth } from '../AuthService';
import { AI_SERVICE_BASE_URL, AI_STREAMING_SERVICE_BASE_URL } from '../AiApiService';

/**
 * Generate a unique identifier for messages
 * @returns {string} Unique identifier
 */
const generateUniqueId = () => {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * AI Conversations API service - Chat and prompt processing
 * Based on AI_API.postman_collection.json endpoints
 * 
 * NOTE: Uses only prompt-with-context endpoint for all operations.
 * This service focuses on chat functionality with the available endpoints.
 */
export const AiConversationsApi = {
  /**
   * Prompt with conversation context and document context
   * Endpoint: POST /api/chat/prompt-with-context
   * @param {string} prompt - User prompt
   * @param {Object} options - Options (conversation_id, document_ids, quality_level)
   * @returns {Promise<Response>} AI response with context
   */
  promptWithConversationContext: async (prompt, options = {}) => {
    const {
      conversation_id = null,
      document_ids = [],
      quality_level = 'balanced',
      endeavor_id = null,
      ...otherOptions
    } = options;

    const requestBody = {
      prompt,
      quality_level,
      ...(conversation_id && { conversation_id }),
      // If endeavor_id is provided, prefer that over document_ids
      ...(endeavor_id ? { endeavor_id } : (document_ids.length > 0 ? { document_ids } : {})),
      ...otherOptions
    };

    console.log('AiConversationsApi: Sending prompt-with-context request:', requestBody);

    return fetchWithAuth(`${AI_SERVICE_BASE_URL}/api/chat/prompt-with-context`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
  },

  /**
   * Continue an existing conversation
   * This is a convenience method that uses prompt-with-context
   * @param {string} prompt - User prompt
   * @param {string|number} conversationId - Conversation ID
   * @param {Object} options - Additional options (quality_level, document_ids)
   * @returns {Promise<Response>} AI response
   */
  continueConversation: async (prompt, conversationId, options = {}) => {
    return AiConversationsApi.promptWithConversationContext(prompt, {
      conversation_id: conversationId,
      ...options
    });
  },

  /**
   * Start a new conversation with documents
   * This uses prompt-with-context without conversation_id to create new conversation
   * @param {string} prompt - Initial prompt
   * @param {Array} documentIds - Document IDs for context
   * @param {Object} options - Additional options (quality_level)
   * @returns {Promise<Response>} AI response (will include new conversation_id)
   */
  startConversationWithDocuments: async (prompt, documentIds = [], options = {}) => {
    return AiConversationsApi.promptWithConversationContext(prompt, {
      document_ids: documentIds,
      ...options
      // Note: Don't include conversation_id to create new conversation
    });
  },

  /**
   * ✅ UPDATED: Chat without any context (general AI chat) - uses prompt-with-context
   * @param {string} prompt - User prompt
   * @param {Object} options - Options (quality_level)
   * @returns {Promise<Response>} AI response
   */
  chatGeneral: async (prompt, options = {}) => {
    // ✅ CHANGED: Use promptWithConversationContext instead of promptSimple
    return AiConversationsApi.promptWithConversationContext(prompt, {
      // No conversation_id or document_ids for general chat
      quality_level: options.quality_level || 'balanced',
      ...options
    });
  },

  // Utility methods (these don't correspond to API endpoints but are helper functions)
  
  /**
   * Get fallback response when AI service is unavailable
   * @param {boolean} hasDocuments - Whether documents are attached
   * @returns {Object} Fallback response
   */
  getFallbackResponse: (hasDocuments = false) => {
    return {
      id: generateUniqueId(),
      text: "I'm having trouble connecting to the AI services right now. Please try again in a moment.",
      sender: 'ai',
      timestamp: new Date(),
      scoutState: 'confused',
      mood: 'apologetic',
      suggestedActions: []
    };
  },

  /**
   * Get welcome message for new conversations
   * @returns {Object} Welcome message
   */
  getWelcomeMessage: () => {
    return {
      id: generateUniqueId(),
      text: "Hello! Welcome to Chat With Your Docs. From here you can select uploaded documents to add context to your conversation. Course-specific chats allow you to chat with every document you have associated with that course.",
      sender: 'ai',
      timestamp: new Date(),
      scoutState: 'greeting',
      mood: 'friendly',
      suggestedActions: []
    };
  },

  /**
   * Get typing indicator message
   * @returns {Object} Typing message
   */
  getTypingMessage: () => {
    return {
      id: generateUniqueId(),
      text: '',
      sender: 'ai',
      timestamp: new Date(),
      isThinking: true,
      scoutState: 'thinking',
      mood: 'focused'
    };
  },

  /**
   * Generate a chat title from the first message
   * @param {Array} messages - Array of messages
   * @returns {string} Generated title
   */
  generateChatTitle: (messages) => {
    if (messages.length === 0) return 'New Chat';
    
    const firstMessage = messages.find(msg => msg.sender === 'user');
    if (firstMessage && firstMessage.text && firstMessage.text.length > 0) {
      return firstMessage.text.substring(0, 30) + (firstMessage.text.length > 30 ? '...' : '');
    }
    return 'New Chat';
  },

  /**
   * Format message for API request
   * @param {Object} message - Message object
   * @returns {Object} Formatted message
   */
  formatMessageForAPI: (message) => {
    return {
      role: message.sender === 'user' ? 'user' : 'assistant',
      content: message.text || message.content,
      tokenCount: message.tokenCount || (message.text ? message.text.split(' ').length : 0),
      timestamp: message.timestamp || new Date(),
      metadata: {
        scoutState: message.scoutState,
        mood: message.mood,
        documentReferences: message.documentReferences,
        suggestedActions: message.suggestedActions
      }
    };
  },

  /**
   * Parse AI response from the chat endpoints
   * @param {Object} apiResponse - Response from AI chat endpoints
   * @returns {Object} Formatted message for UI
   */
  parseAIResponse: (apiResponse) => {
    console.log('Parsing AI response:', apiResponse);
    
    return {
      id: generateUniqueId(),
      text: apiResponse.response || apiResponse.content || apiResponse.answer || 'No response received',
      sender: 'ai',
      timestamp: new Date(),
      scoutState: 'thinking',
      mood: 'helpful',
      documentReferences: apiResponse.chunks_metadata || apiResponse.sources || [],
      conversationId: apiResponse.conversation_id || null,
      tokenCount: apiResponse.tokens_used || null,
      qualityLevel: apiResponse.quality_level || 'balanced'
    };
  },

  /**
   * Get quality level options for UI
   * @returns {Array} Quality level options
   */
  getQualityLevelOptions: () => {
    return [
      { value: 'low', label: '⚡ Fast', description: 'Quick responses, basic quality' },
      { value: 'balanced', label: '⚖️ Balanced', description: 'Good balance of speed and quality' },
      { value: 'high', label: '🎯 Precise', description: 'High quality, slower responses' },
      { value: 'deep_research', label: '🧠 Deep Research', description: 'Most thorough reasoning & context (slowest)' }
    ];
  }
};

/**
 * Stream prompt with conversation/doc/course context as SSE (POST)
 * Handlers: { onMeta(data), onToken(textChunk), onDone(data), onError(error) }
 * Returns: { cancel: () => void }
 */
export const AiConversationsStream = {
  promptWithContextStream: async (prompt, options = {}, handlers = {}) => {
    const {
      conversation_id = null,
      document_ids = [],
      quality_level = 'balanced',
      endeavor_id = null,
      signal: externalSignal,
      ...otherOptions
    } = options;

    const body = {
      prompt,
      quality_level,
      ...(conversation_id && { conversation_id }),
      ...(endeavor_id ? { endeavor_id } : (document_ids.length > 0 ? { document_ids } : {})),
      ...otherOptions
    };

    const ctrl = new AbortController();
    const signal = externalSignal || ctrl.signal;

    try {
      const resp = await fetchWithAuth(`${AI_STREAMING_SERVICE_BASE_URL}/api/chat/prompt-with-context/stream`, {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'X-Accel-Buffering': 'no' // helps Nginx not buffer
        },
        body: JSON.stringify(body),
        signal
      });

      if (!resp.ok || !resp.body) {
        const text = await resp.text().catch(() => '');
        handlers.onError?.(text || `Stream failed (${resp.status})`);
        return { cancel: () => ctrl.abort() };
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      const flushBlocks = () => {
        // Split on blank line (handles \n\n and \r\n\r\n)
        const blocks = buffer.split(/\r?\n\r?\n/);
         // keep last partial (no trailing blank line)
        buffer = blocks.pop() || '';
         for (const blk of blocks) {
          const lines = blk.split(/\r?\n/);
           let event = 'message';
           const dataLines = [];
           for (const ln of lines) {
             if (ln.startsWith('event:')) event = ln.slice(6).trim();
             else if (ln.startsWith('data:')) dataLines.push(ln.slice(5).trim());
           }
           const dataRaw = dataLines.join('\n');
           let payload = dataRaw;
           try { payload = dataRaw ? JSON.parse(dataRaw) : dataRaw; } catch {}
           switch (event) {
             case 'meta': handlers.onMeta?.(payload); break;
             case 'reasoning': handlers.onReasoning?.(payload); break;
             case 'token': handlers.onToken?.(typeof payload === 'string' ? payload : (payload?.text ?? '')); break;
             case 'done': handlers.onDone?.(payload); break;
             case 'error': handlers.onError?.(payload || 'Stream error'); break;
             default: /* ignore */ break;
           }
         }
       };

      (async () => {
        try {
          // stream loop
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            flushBlocks();
          }
          // flush any trailing complete block
          if (buffer.includes('\n\n')) flushBlocks();
        } catch (e) {
          if (signal.aborted) return;
          handlers.onError?.(e?.message || 'Stream interrupted');
        }
      })();

      return { cancel: () => ctrl.abort() };
    } catch (e) {
      if (!signal.aborted) handlers.onError?.(e?.message || 'Stream failed');
      return { cancel: () => ctrl.abort() };
    }
  }
};